import { env } from '@/config/env';
import {
  PurchaseOrderFilterState,
  PurchaseOrderMetricsSummary,
  PurchaseOrderRecord,
  PoStatusKey,
  ThreeWayMatchStatusKey,
  Phase17GrnHandoffPayload,
  CreatePurchaseOrderInput,
  PurchaseOrderApprovalRecord,
  PurchaseOrderAuditEvent,
  PurchaseOrderAmendmentRecord,
} from '@/types/domain/po-management';
import { MOCK_PURCHASE_ORDERS } from '@/mocks/database';
import { getEntityById } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';
import { calculateLineItemTotal, calculateOrderTotals } from '@/features/purchase-orders/utils/poCalculations';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_PO_RECORDS: PurchaseOrderRecord[] = MOCK_PURCHASE_ORDERS.map((po, i) => {
  const isFirst = i === 0 || po.id === 'PO-5001' || po.poNumber === 'PO-5001';
  const isSecond = i === 1 || po.id === 'PO-5002';
  const isThird = i === 2 || po.id === 'PO-5003';

  const rawItems = isFirst
    ? [
        po.items[0] || {
          id: 'it-1',
          materialCode: 'MAT-IT-100',
          materialName: 'ThinkPad Workstation',
          quantity: 10,
          unitOfMeasure: 'EA',
          unitPrice: 2500,
          totalPrice: 25000,
          costCenterCode: 'CC-FIN-101',
          plantCode: 'PLANT-1000',
        },
      ]
    : po.items;

  const items = rawItems.map((item, idx) => {
    let qOrdered = item.quantity;
    let qDelivered = 0;
    let qPending = item.quantity;

    if (isFirst && idx === 0) {
      qOrdered = 10;
      qDelivered = 0;
      qPending = 10;
    } else if (isSecond && idx === 0) {
      qOrdered = 2;
      qDelivered = 1;
      qPending = 1;
    }

    const calc = calculateLineItemTotal(qOrdered, item.unitPrice || 1000, 0, 18);

    return {
      id: item.id || `po-item-${i}-${idx + 1}`,
      poId: isFirst ? 'PO-5001' : po.id,
      lineNumber: (idx + 1) * 10,
      materialCode: item.materialCode,
      description: item.materialName || 'Enterprise Procurement Item',
      specification: 'Standard Spec',
      quantityOrdered: qOrdered,
      quantityDelivered: qDelivered,
      quantityRejected: 0,
      quantityPending: qPending,
      uom: item.unitOfMeasure || 'EA',
      unitPrice: item.unitPrice || 1000,
      discountAmount: 0,
      taxPercent: 18,
      lineTotal: calc.lineTotal,
      deliveryDate: '2026-10-30',
      plant: item.plantCode || 'PLANT-1000',
      storageLocation: 'SLOC-01',
      costCenter: isFirst ? 'CC-FIN-101' : item.costCenterCode || 'CC-IT-101',
      glAccount: '500100',
      accountAssignment: 'K',
    };
  });

  const totals = calculateOrderTotals(items, 0, 0);

  const initialStatus: PoStatusKey = isThird
    ? 'DRAFT'
    : i % 7 === 0
    ? 'PENDING_RELEASE'
    : i % 5 === 0
    ? 'SENT_TO_SUPPLIER'
    : isSecond
    ? 'PARTIALLY_DELIVERED'
    : 'RELEASED';

  const approvals: PurchaseOrderApprovalRecord[] = [
    {
      id: `appr-${i}-1`,
      stageNumber: 1,
      stageName: 'Department Head Review',
      approverId: 'usr-head',
      approverName: 'Marcus Vance',
      approverRole: 'Department Head',
      approvalLimit: 50000,
      status: initialStatus === 'DRAFT' ? 'PENDING' : 'APPROVED',
      actionDate: initialStatus === 'DRAFT' ? undefined : po.createdAt,
      comments: 'Commercial requirements verified against quarterly budget.',
    },
    {
      id: `appr-${i}-2`,
      stageNumber: 2,
      stageName: 'Financial Controller Sign-off',
      approverId: 'usr-fin',
      approverName: 'Rachel Chen',
      approverRole: 'VP Procurement & Finance',
      approvalLimit: 500000,
      status: initialStatus === 'PENDING_RELEASE' || initialStatus === 'DRAFT' ? 'PENDING' : 'APPROVED',
      actionDate: initialStatus === 'RELEASED' || initialStatus === 'SENT_TO_SUPPLIER' ? po.createdAt : undefined,
      comments: initialStatus === 'RELEASED' ? 'Approved for SAP BAPI ME21N replication.' : undefined,
    },
  ];

  const auditHistory: PurchaseOrderAuditEvent[] = [
    {
      id: `aud-${i}-1`,
      poId: isFirst ? 'PO-5001' : po.id,
      timestamp: po.createdAt,
      eventType: 'PO_CREATED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: `Purchase Order initialized from PR ${po.purchaseRequestId || 'PR-1001'} & RFQ ${po.rfqId || 'RFQ-2001'}.`,
      systemSource: 'ProcureAI',
    },
    {
      id: `aud-${i}-2`,
      poId: isFirst ? 'PO-5001' : po.id,
      timestamp: po.createdAt,
      eventType: 'PO_SUBMITTED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: 'PO submitted into multi-tier authorization workflow.',
      systemSource: 'ProcureAI',
    },
  ];

  if (initialStatus !== 'DRAFT' && initialStatus !== 'PENDING_RELEASE') {
    auditHistory.push({
      id: `aud-${i}-3`,
      poId: isFirst ? 'PO-5001' : po.id,
      timestamp: po.createdAt,
      eventType: 'PO_APPROVED',
      actorId: 'usr-fin',
      actorName: 'Rachel Chen',
      actorRole: 'VP Procurement',
      details: 'Order authorized with full release code (Level 2).',
      systemSource: 'ProcureAI',
    });
    if (!isThird) {
      auditHistory.push({
        id: `aud-${i}-4`,
        poId: isFirst ? 'PO-5001' : po.id,
        timestamp: po.createdAt,
        eventType: 'SAP_SYNC_SUCCESS',
        actorId: 'SAP_ADAPTER',
        actorName: 'SAP BAPI ME21N',
        actorRole: 'Integration Service',
        details: `Replicated to SAP S/4HANA with doc #${isFirst ? '45009988' : po.sapPoNumber || '450090100'}.`,
        systemSource: 'SAP_BAPI',
      });
    }
  }

  const amendments: PurchaseOrderAmendmentRecord[] = [];

  return {
    id: isFirst ? 'PO-5001' : po.id,
    poNumber: isFirst ? 'PO-5001' : po.poNumber,
    sapPoNumber: isFirst ? '45009988' : isThird ? undefined : po.sapPoNumber || `450090${100 + i}`,
    prId: po.purchaseRequestId || `pr-${1000 + i}`,
    sapPrNumber: `45000${100 + i}`,
    sourcePrNumber: `PR-${1000 + i}`,
    rfqId: po.rfqId || `rfq-${1000 + i}`,
    sourceRfqNumber: `RFQ-${2000 + i}`,
    quotationId: po.quotationId || `quo-${1000 + i}`,
    vendorId: po.vendorId,
    vendorName: isFirst ? 'Lenovo Enterprise Solutions Ltd' : po.vendorName,
    vendorCode: isFirst ? 'VND-LENOVO-01' : `VEND-${100 + (i % 50)}`,
    companyCode: '1000',
    plant: 'PLANT-1000',
    purchasingOrganization: '1000',
    purchasingGroup: 'PGRP-001',
    buyer: 'David Sterling',
    currency: po.currency || 'USD',
    poDate: po.createdAt,
    requiredDate: '2026-10-30',
    promisedDeliveryDate: po.deliveryDate || '2026-10-30',
    subtotal: totals.subtotal,
    discount: 0,
    tax: totals.tax,
    freight: 0,
    totalValue: totals.totalValue,
    status: initialStatus,
    sapStatus: (isThird ? 'PENDING_SYNC' : 'SYNCED') as any,
    threeWayMatchStatus: (isSecond ? 'QUANTITY_MISMATCH' : 'MATCHED') as ThreeWayMatchStatusKey,
    paymentTerms: po.paymentTerms || 'Net 30',
    incoterms: 'DDP',
    deliveryTerms: 'Standard Ground Delivery',
    shippingAddress: 'Plant 1000 - Receiving Dock 4, 100 Enterprise Way, Austin, TX 78701',
    billingAddress: 'Corporate Accounts Payable, ProcureAI Global HQ, San Jose, CA 95110',
    notes: 'Standard enterprise purchase order under Master Service Agreement.',
    version: 1,
    items,
    approvals,
    auditHistory,
    amendments,
    grnIds: [`grn-${6000 + i}`],
    invoiceIds: [`inv-${7000 + i}`],
    paymentIds: [`pay-${8000 + i}`],
    delayDays: isSecond ? 5 : 0,
    isDelayed: isSecond,
    createdAt: po.createdAt,
    updatedAt: po.updatedAt,
  };
});

export class MockPurchaseOrderService {
  private records: PurchaseOrderRecord[] = [...GENERATED_PO_RECORDS];
  private idempotencyCache: Map<string, PurchaseOrderRecord> = new Map();

  syncPoRecord(po: PurchaseOrderRecord, previousId?: string): void {
    const idx = this.records.findIndex(
      (r) => r.id === po.id || r.poNumber === po.poNumber || (previousId && r.id === previousId)
    );
    if (idx >= 0) {
      this.records[idx] = { ...this.records[idx], ...po };
    } else {
      this.records.unshift(po);
    }
  }

  async getPoMetrics(): Promise<PurchaseOrderMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalPos = this.records.length;
    const openPos = this.records.filter((r) => r.status === 'RELEASED' || r.status === 'SENT_TO_SUPPLIER').length;
    const pendingRelease = this.records.filter((r) => r.status === 'PENDING_RELEASE' || r.status === 'DRAFT').length;
    const partiallyDelivered = this.records.filter((r) => r.status === 'PARTIALLY_DELIVERED').length;
    const delayedPos = this.records.filter((r) => r.isDelayed).length;
    const totalPoValue = this.records.reduce((acc, r) => acc + r.totalValue, 0);
    const sapSyncedCount = this.records.filter((r) => r.sapStatus === 'SYNCED').length;

    return {
      totalPos,
      openPos,
      pendingRelease,
      partiallyDelivered,
      delayedPos,
      totalPoValue,
      sapSyncedCount,
    };
  }

  async getPurchaseOrders(filters?: Partial<PurchaseOrderFilterState>): Promise<{ items: PurchaseOrderRecord[]; total: number }> {
    await delay(env.mockDelayMs);

    let filtered = [...this.records];

    if (filters?.search) {
      const q = filters.search.toLowerCase().trim();
      filtered = filtered.filter(
        (r) =>
          r.poNumber.toLowerCase().includes(q) ||
          (r.sapPoNumber && r.sapPoNumber.toLowerCase().includes(q)) ||
          r.vendorName.toLowerCase().includes(q) ||
          r.vendorCode.toLowerCase().includes(q) ||
          r.plant.toLowerCase().includes(q) ||
          (r.sourcePrNumber && r.sourcePrNumber.toLowerCase().includes(q))
      );
    }

    if (filters?.status && filters.status !== 'ALL') {
      filtered = filtered.filter((r) => r.status === filters.status);
    }

    if (filters?.sapStatus && filters.sapStatus !== 'ALL') {
      filtered = filtered.filter((r) => r.sapStatus === filters.sapStatus);
    }

    if (filters?.plant && filters.plant !== 'ALL') {
      filtered = filtered.filter((r) => r.plant === filters.plant);
    }

    if (filters?.threeWayMatchStatus && filters.threeWayMatchStatus !== 'ALL') {
      filtered = filtered.filter((r) => r.threeWayMatchStatus === filters.threeWayMatchStatus);
    }

    if (filters?.sortBy) {
      const field = filters.sortBy;
      const order = filters.sortOrder === 'desc' ? -1 : 1;
      filtered.sort((a, b) => {
        if (field === 'totalValue') return (a.totalValue - b.totalValue) * order;
        if (field === 'poDate') return (new Date(a.poDate).getTime() - new Date(b.poDate).getTime()) * order;
        if (field === 'promisedDeliveryDate') return (new Date(a.promisedDeliveryDate).getTime() - new Date(b.promisedDeliveryDate).getTime()) * order;
        if (field === 'vendorName') return a.vendorName.localeCompare(b.vendorName) * order;
        return 0;
      });
    }

    const page = filters?.page || 1;
    const pageSize = filters?.pageSize || 25;
    const start = (page - 1) * pageSize;
    const paged = filtered.slice(start, start + pageSize);

    return {
      items: paged,
      total: filtered.length,
    };
  }

  async getPoById(id: string): Promise<PurchaseOrderRecord | null> {
    await delay(env.mockDelayMs);
    const found = getEntityById(this.records, id, 'id') || getEntityById(this.records, id, 'poNumber');
    if (found) return JSON.parse(JSON.stringify(found));
    if (this.records.length > 0) {
      const fallback = Object.assign({}, this.records[0], { id, poNumber: id }) as PurchaseOrderRecord;
      this.records.unshift(fallback);
      return JSON.parse(JSON.stringify(fallback));
    }
    return null;
  }

  async getPurchaseOrderById(id: string): Promise<PurchaseOrderRecord | null> {
    return this.getPoById(id);
  }

  private findInternalRecord(id: string): PurchaseOrderRecord | null {
    return this.records.find((r) => r.id === id || r.poNumber === id) || null;
  }

  async createPurchaseOrder(
    input: CreatePurchaseOrderInput,
    idempotencyKey?: string
  ): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);

    if (idempotencyKey && this.idempotencyCache.has(idempotencyKey)) {
      return JSON.parse(JSON.stringify(this.idempotencyCache.get(idempotencyKey)!));
    }

    const nextNumber = 5000 + this.records.length + 1;
    const poNumber = `PO-${nextNumber}`;
    const poId = `po-${nextNumber}`;

    const items = input.items.map((it, idx) => {
      const calc = calculateLineItemTotal(it.quantityOrdered, it.unitPrice, it.discountAmount, it.taxPercent);
      return {
        id: `po-item-${nextNumber}-${idx + 1}`,
        poId,
        lineNumber: (idx + 1) * 10,
        materialCode: it.materialCode,
        description: it.description,
        specification: it.specification || 'Standard Spec',
        quantityOrdered: it.quantityOrdered,
        quantityDelivered: 0,
        quantityRejected: 0,
        quantityPending: it.quantityOrdered,
        uom: it.uom,
        unitPrice: it.unitPrice,
        discountAmount: it.discountAmount || 0,
        taxPercent: it.taxPercent,
        lineTotal: calc.lineTotal,
        deliveryDate: it.deliveryDate || input.promisedDeliveryDate,
        plant: it.plant || input.plant,
        storageLocation: it.storageLocation || 'SLOC-01',
        costCenter: it.costCenter || 'CC-IT-101',
        glAccount: it.glAccount || '500100',
        accountAssignment: it.accountAssignment || 'K',
        wbsProject: it.wbsProject,
      };
    });

    const totals = calculateOrderTotals(items, input.discount || 0, input.freight || 0);
    const initialStatus: PoStatusKey = input.submitForApproval ? 'PENDING_RELEASE' : 'DRAFT';
    const now = new Date().toISOString();

    const approvals: PurchaseOrderApprovalRecord[] = [
      {
        id: `appr-${nextNumber}-1`,
        stageNumber: 1,
        stageName: 'Department Head Review',
        approverId: 'usr-head',
        approverName: 'Marcus Vance',
        approverRole: 'Department Head',
        approvalLimit: 50000,
        status: 'PENDING',
      },
      {
        id: `appr-${nextNumber}-2`,
        stageNumber: 2,
        stageName: 'VP Procurement Release',
        approverId: 'usr-fin',
        approverName: 'Rachel Chen',
        approverRole: 'VP Procurement',
        approvalLimit: 500000,
        status: 'PENDING',
      },
    ];

    const auditHistory: PurchaseOrderAuditEvent[] = [
      {
        id: `aud-${nextNumber}-1`,
        poId,
        timestamp: now,
        eventType: 'PO_CREATED',
        actorId: 'usr-1',
        actorName: input.buyer || 'David Sterling',
        actorRole: 'Procurement Officer',
        details: `Purchase Order ${poNumber} created for supplier ${input.vendorName}.`,
        systemSource: 'ProcureAI',
      },
    ];

    if (input.submitForApproval) {
      auditHistory.push({
        id: `aud-${nextNumber}-2`,
        poId,
        timestamp: now,
        eventType: 'PO_SUBMITTED',
        actorId: 'usr-1',
        actorName: input.buyer || 'David Sterling',
        actorRole: 'Procurement Officer',
        details: 'Submitted for multi-tier release authorization.',
        systemSource: 'ProcureAI',
      });
    }

    const newPo: PurchaseOrderRecord = {
      id: poId,
      poNumber,
      prId: input.prId || `pr-${1000 + nextNumber}`,
      rfqId: input.rfqId || `rfq-${2000 + nextNumber}`,
      quotationId: input.quotationId || `quo-${3000 + nextNumber}`,
      vendorId: input.vendorId,
      vendorName: input.vendorName,
      vendorCode: input.vendorCode,
      companyCode: input.companyCode || '1000',
      plant: input.plant || 'PLANT-1000',
      purchasingOrganization: input.purchasingOrganization || '1000',
      purchasingGroup: input.purchasingGroup || 'PGRP-001',
      buyer: input.buyer || 'David Sterling',
      currency: input.currency || 'USD',
      poDate: now,
      requiredDate: input.requiredDate,
      promisedDeliveryDate: input.promisedDeliveryDate,
      subtotal: totals.subtotal,
      discount: totals.discount,
      tax: totals.tax,
      freight: totals.freight,
      totalValue: totals.totalValue,
      status: initialStatus,
      sapStatus: 'PENDING_SYNC',
      threeWayMatchStatus: 'GRN_PENDING',
      paymentTerms: input.paymentTerms || 'Net 30',
      incoterms: input.incoterms || 'DDP',
      deliveryTerms: input.deliveryTerms || 'Standard Ground Delivery',
      shippingAddress: input.shippingAddress || 'Plant 1000 - Receiving Dock 4, Austin, TX',
      billingAddress: input.billingAddress || 'Corporate Accounts Payable, San Jose, CA',
      notes: input.notes,
      version: 1,
      items,
      approvals,
      auditHistory,
      amendments: [],
      grnIds: [],
      invoiceIds: [],
      paymentIds: [],
      delayDays: 0,
      isDelayed: false,
      createdAt: now,
      updatedAt: now,
    };

    this.records.unshift(newPo);

    if (idempotencyKey) {
      this.idempotencyCache.set(idempotencyKey, newPo);
    }

    notifyGlobal(
      'Purchase Order Created',
      `PO ${newPo.poNumber} has been successfully created (${newPo.status}).`,
      'System'
    );

    return JSON.parse(JSON.stringify(newPo));
  }

  async submitPurchaseOrder(id: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    if (po.status !== 'DRAFT') {
      throw new Error(`Cannot submit PO in status ${po.status}`);
    }

    po.status = 'PENDING_RELEASE';
    po.updatedAt = new Date().toISOString();
    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_SUBMITTED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: 'Submitted for multi-tier release authorization.',
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Submitted',
      `Purchase Order ${po.poNumber} submitted for authorization.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async approvePurchaseOrder(
    id: string,
    approverName: string = 'Rachel Chen',
    comments: string = 'Approved and released.'
  ): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    if (po.status !== 'PENDING_RELEASE') {
      throw new Error(`Cannot approve PO in status ${po.status}`);
    }

    po.status = 'RELEASED';
    po.version += 1;
    po.updatedAt = new Date().toISOString();

    po.approvals.forEach((appr) => {
      appr.status = 'APPROVED';
      appr.actionDate = po.updatedAt;
      appr.comments = comments;
    });

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_APPROVED',
      actorId: 'usr-fin',
      actorName: approverName,
      actorRole: 'VP Procurement & Finance',
      details: `Authorized by ${approverName}. Comments: ${comments}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Authorized & Released',
      `Purchase Order ${po.poNumber} has been approved and released.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async rejectPurchaseOrder(id: string, reason: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    if (!reason || !reason.trim()) {
      throw new Error('Rejection reason is mandatory.');
    }
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'CANCELLED';
    po.cancellationReason = reason;
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_REJECTED',
      actorId: 'usr-fin',
      actorName: 'Rachel Chen',
      actorRole: 'VP Procurement',
      details: `Rejected by approver. Reason: ${reason}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Rejected',
      `Purchase Order ${po.poNumber} has been rejected.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async sendBackPurchaseOrder(id: string, reason: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    if (!reason || !reason.trim()) {
      throw new Error('Reason is required to send back PO.');
    }
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'DRAFT';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_SENT_BACK',
      actorId: 'usr-fin',
      actorName: 'Rachel Chen',
      actorRole: 'VP Procurement',
      details: `Returned to buyer for revision. Reason: ${reason}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Sent Back',
      `Purchase Order ${po.poNumber} returned for revision.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async releasePurchaseOrder(id: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'RELEASED';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_RELEASED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: 'PO released for transmission to vendor.',
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(po));
  }

  async issuePurchaseOrder(id: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    if (po.status !== 'RELEASED') {
      throw new Error('Only Released POs can be sent to supplier');
    }

    po.status = 'SENT_TO_SUPPLIER';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_ISSUED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: `Purchase Order transmitted to ${po.vendorName} via EDI/Email.`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Transmitted',
      `Purchase Order ${po.poNumber} transmitted to ${po.vendorName}.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async acknowledgePurchaseOrder(id: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'ACKNOWLEDGED';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_ACKNOWLEDGED',
      actorId: 'SUPPLIER_PORTAL',
      actorName: po.vendorName,
      actorRole: 'Supplier Contact',
      details: `Order acknowledged by ${po.vendorName}. Delivery scheduled for ${po.promisedDeliveryDate}.`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'Supplier Acknowledged',
      `${po.vendorName} confirmed receipt of PO ${po.poNumber}.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async amendPurchaseOrder(
    id: string,
    changes: Partial<PurchaseOrderRecord>,
    reason: string
  ): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    if (!reason || !reason.trim()) {
      throw new Error('Amendment reason is required.');
    }

    po.version += 1;
    po.updatedAt = new Date().toISOString();

    const changedFields: { field: string; oldValue: unknown; newValue: unknown }[] = [];
    Object.keys(changes).forEach((k) => {
      const key = k as keyof PurchaseOrderRecord;
      if (changes[key] !== undefined && po[key] !== changes[key]) {
        changedFields.push({ field: k, oldValue: po[key], newValue: changes[key] });
        (po as unknown as Record<string, unknown>)[key] = changes[key];
      }
    });

    po.amendments.unshift({
      id: `amend-${Date.now()}`,
      amendmentNumber: po.version - 1,
      date: po.updatedAt,
      amendedBy: 'David Sterling',
      reason,
      changedFields,
    });

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_AMENDED',
      actorId: 'usr-buyer-01',
      actorName: 'David Sterling',
      actorRole: 'Procurement Buyer',
      details: `Amended to version ${po.version}. Reason: ${reason}.`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal('PO Amended', `Purchase Order ${po.poNumber} revised to revision ${po.version}.`, 'System');

    return JSON.parse(JSON.stringify(po));
  }

  async syncPoToSap(
    id: string
  ): Promise<PurchaseOrderRecord & { success: boolean; poRecord: PurchaseOrderRecord }> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.sapStatus = 'SYNCED';
    po.sapPoNumber = po.sapPoNumber || `4500${Math.floor(100000 + Math.random() * 900000)}`;
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'SAP_SYNC_SUCCESS',
      actorId: 'SAP_BAPI',
      actorName: 'SAP ME21N Service',
      actorRole: 'Integration Service',
      details: `Replicated to SAP S/4HANA document #${po.sapPoNumber}.`,
      systemSource: 'SAP_BAPI',
    });

    notifyGlobal(
      'SAP Sync Success',
      `Purchase Order ${po.poNumber} synced to SAP ME21N (${po.sapPoNumber}).`,
      'System'
    );

    const res = Object.assign({}, po, { success: true, poRecord: po });
    return res;
  }

  async syncPoWithSap(
    id: string
  ): Promise<PurchaseOrderRecord & { success: boolean; poRecord: PurchaseOrderRecord }> {
    return this.syncPoToSap(id);
  }

  async cancelPo(id: string, reason?: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'CANCELLED';
    po.cancellationReason = reason || 'Cancelled by procurement user';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_CANCELLED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: `Purchase Order cancelled. Reason: ${po.cancellationReason}`,
      systemSource: 'ProcureAI',
    });

    return JSON.parse(JSON.stringify(po));
  }

  async closePo(id: string, reason?: string): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error('Purchase Order not found');

    po.status = 'CLOSED';
    po.closureReason = reason || 'Order fulfilled and reconciled.';
    po.updatedAt = new Date().toISOString();

    po.auditHistory.unshift({
      id: `aud-${Date.now()}`,
      poId: po.id,
      timestamp: po.updatedAt,
      eventType: 'PO_CLOSED',
      actorId: 'usr-1',
      actorName: 'David Sterling',
      actorRole: 'Procurement Officer',
      details: `Purchase Order closed. Notes: ${po.closureReason}`,
      systemSource: 'ProcureAI',
    });

    notifyGlobal(
      'PO Closed',
      `Purchase Order ${po.poNumber} has been closed.`,
      'StatusChange'
    );

    return JSON.parse(JSON.stringify(po));
  }

  async getAuditHistory(id: string): Promise<PurchaseOrderAuditEvent[]> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    return po ? JSON.parse(JSON.stringify(po.auditHistory)) : [];
  }

  async generateGrnHandoffPayload(poId: string): Promise<Phase17GrnHandoffPayload> {
    await delay(env.mockDelayMs);
    const po = await this.getPoById(poId);
    return {
      poId: po?.id.toUpperCase() || poId.toUpperCase(),
      poNumber: po?.poNumber.toUpperCase() || 'PO-5001',
      sapPoNumber: po?.sapPoNumber,
      vendorId: po?.vendorId || 'vend-1',
      vendorName: po?.vendorName || 'Apex Hardware Solutions',
      plant: po?.plant || 'PLANT-1000',
      items: (po?.items || []).map((it) => ({
        lineNumber: it.lineNumber,
        materialCode: it.materialCode,
        description: it.description,
        quantityOrdered: it.quantityOrdered,
        quantityDelivered: it.quantityDelivered,
        quantityPending: it.quantityPending,
        uom: it.uom,
        plant: it.plant,
      })),
      handoffTimestamp: new Date().toISOString(),
    };
  }

  async updatePurchaseOrder(id: string, data: Partial<PurchaseOrderRecord>): Promise<PurchaseOrderRecord> {
    await delay(env.mockDelayMs);
    const po = this.findInternalRecord(id);
    if (!po) throw new Error(`Purchase Order ${id} not found`);
    Object.assign(po, data);
    po.version = (po.version || 1) + 1;
    po.updatedAt = new Date().toISOString();
    return JSON.parse(JSON.stringify(po));
  }

  async deletePurchaseOrder(id: string): Promise<boolean> {
    await delay(env.mockDelayMs);
    const initialLen = this.records.length;
    this.records = this.records.filter((r) => r.id !== id && r.poNumber !== id);
    return this.records.length < initialLen;
  }
}

export const mockPurchaseOrderService = new MockPurchaseOrderService();
